import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Home, CheckCircle2, Car, Shield, Dumbbell, Wind, TreePine, Waves, Phone, Users, Presentation, Sparkles, Building, LogIn, Anchor, Upload, X, Image, ArrowLeft, ArrowRight, Mail, AlertTriangle, Sofa, UtensilsCrossed, Sun, Flame, Compass, Fan, Plug, Armchair, CircleDot, Zap, Baby, ParkingCircle, Plus, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import { trackEvent } from '../utils/analytics';
import { uploadImages, describeFailures } from '../lib/uploadImage';
import { formatIndianNumber, parseNumber } from '../lib/indianFormat';

const resolveImg = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  const BE = process.env.REACT_APP_BACKEND_URL;
  return `${BE}/${url.replace(/^\/+/, '')}`;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AMENITIES_LIST = [
  { id: 'parking', label: 'Parking', icon: Car },
  { id: 'security', label: '24/7 Security', icon: Shield },
  { id: 'gym', label: 'Gym', icon: Dumbbell },
  { id: 'ac', label: 'AC', icon: Wind },
  { id: 'garden', label: 'Garden', icon: TreePine },
  { id: 'waterfront', label: 'Waterfront', icon: Anchor },
  { id: 'sauna', label: 'Sauna', icon: Sparkles },
  { id: 'steam', label: 'Steam Room', icon: Waves },
  { id: 'pool', label: 'Swimming Pool', icon: Waves },
  { id: 'intercom', label: 'Intercom', icon: Phone },
  { id: 'clubhouse', label: 'Club House', icon: Building },
  { id: 'meeting_room', label: 'Meeting Room', icon: Presentation },
  { id: 'furnished', label: 'Furnished', icon: Sofa },
  { id: 'semi_furnished', label: 'Semi-Furnished', icon: Armchair },
  { id: 'equipped_kitchen', label: 'Equipped Kitchen', icon: UtensilsCrossed },
  { id: 'solar_power', label: 'Solar Power', icon: Sun },
  { id: 'heater', label: 'Heater', icon: Flame },
  { id: 'fan', label: 'Fan', icon: Fan },
  { id: 'electrical_provisions', label: 'Electrical Provisions', icon: Plug },
  { id: 'vastu_compliant', label: 'Vastu Compliant', icon: Compass },
  { id: 'badminton_court', label: 'Badminton Court', icon: CircleDot },
  { id: 'basketball_court', label: 'Basketball Court', icon: CircleDot },
  { id: 'yoga', label: 'Yoga', icon: Users },
  { id: 'ev_charging', label: 'EV Charging', icon: Zap },
  { id: 'childrens_play_area', label: "Children's Play Area", icon: Baby },
  { id: 'basement_parking', label: 'Basement Parking', icon: ParkingCircle },
  { id: 'smart_home', label: 'Smart Home', icon: Home },
  { id: 'amphitheater', label: 'Amphitheater', icon: Presentation },
  { id: 'jogging_track', label: 'Jogging Track', icon: Dumbbell },
  { id: 'reflexology_path', label: 'Reflexology Path', icon: TreePine },
  { id: 'drivers_room', label: 'Drivers Room', icon: Car },
  { id: 'terrace', label: 'Terrace', icon: Home },
  { id: 'kids_pool', label: 'Kids Pool', icon: Waves },
  { id: 'tennis_court', label: 'Tennis Court', icon: CircleDot },
  { id: 'pickleball_court', label: 'Pickleball Court', icon: CircleDot },
  { id: 'wall_climbing', label: 'Wall Climbing', icon: Dumbbell },
  { id: 'jacuzzi', label: 'Jacuzzi', icon: Waves },
  { id: 'paddle_court', label: 'Paddle Court', icon: CircleDot },
  { id: 'maids_room', label: 'Maids Room', icon: Home },
  { id: 'study_room', label: 'Study Room', icon: Presentation },
];

const ListProperty = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [listingType, setListingType] = useState('buy');
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [propertyStatus, setPropertyStatus] = useState('ready');
  const [connectivity, setConnectivity] = useState({
    railway_station: '',
    hospital: '',
    police_station: '',
    school: '',
    mall: '',
    airport: ''
  });
  const [user, setUser] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, percent: 0 });
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [formStep, setFormStep] = useState(1);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    property_type: 'apartment',
    city: '',
    locality: '',
    price: '',
    size_sqft: '',
    land_size: '',
    bedrooms: '',
    bathrooms: '',
    price_onwards: false,
    size_sqft_onwards: false,
    bedrooms_onwards: false,
    images: '',
    seller_type: 'owner',
    seller_name: '',
    seller_phone: '',
    seller_email: '',
    location_link: '',
    show_price_per_sqft: false,
    price_per_cent: '',
    rent_period: 'per_month'
  });
  const [countryCode, setCountryCode] = useState('+91');
  const [unitTypes, setUnitTypes] = useState([]);
  const [reraImages, setReraImages] = useState([]);
  const [uploadingRera, setUploadingRera] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (storedUser && token) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      // Pre-fill seller info from user profile
      setFormData(prev => ({
        ...prev,
        seller_name: userData.name || '',
        seller_email: userData.email || '',
        seller_phone: userData.phone ? userData.phone.replace(/^\+?91/, '') : ''
      }));
    }
    setCheckingAuth(false);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleAmenity = (amenityId) => {
    setSelectedAmenities(prev => 
      prev.includes(amenityId) 
        ? prev.filter(id => id !== amenityId)
        : [...prev, amenityId]
    );
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (uploadedImages.length + files.length > 20) {
      alert('Maximum 20 images allowed');
      return;
    }
    setUploading(true);
    setUploadProgress({ current: 0, total: files.length, percent: 0 });
    try {
      const { uploaded, failed } = await uploadImages(API, files, (progress) => {
        setUploadProgress(progress);
      });
      for (const item of uploaded) {
        setUploadedImages(prev => [...prev, { url: item.url, preview: URL.createObjectURL(item.file) }]);
      }
      if (failed.length) {
        alert(`${uploaded.length} of ${files.length} images uploaded successfully.\n\nFailed:\n${describeFailures(failed)}\n\nThe successful images have been added.`);
      }
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const moveImage = (index, direction) => {
    setUploadedImages(prev => {
      const newArr = [...prev];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= newArr.length) return prev;
      [newArr[index], newArr[targetIndex]] = [newArr[targetIndex], newArr[index]];
      return newArr;
    });
  };

  const setAsCover = (index) => {
    setUploadedImages(prev => {
      const newArr = [...prev];
      const [moved] = newArr.splice(index, 1);
      newArr.unshift(moved);
      return newArr;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        listing_type: listingType,
        price: parseFloat(formData.price),
        size: formData.size_sqft ? `${formData.size_sqft} sq.ft` : '',
        size_sqft: formData.size_sqft ? parseFloat(formData.size_sqft) : null,
        land_size: formData.land_size || null,
        bedrooms: formData.bedrooms ? parseFloat(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        price_onwards: formData.price_onwards || false,
        size_sqft_onwards: formData.size_sqft_onwards || false,
        bedrooms_onwards: formData.bedrooms_onwards || false,
        unit_types: (propertyStatus === 'off_plan' || formData.seller_type === 'builder') && unitTypes.length > 0 ? unitTypes.map(u => ({...u, price: u.price ? parseFloat(parseNumber(u.price)) : ''})) : null,
        images: uploadedImages.map(img => img.url),
        amenities: selectedAmenities,
        property_status: propertyStatus,
        rera_number: formData.rera_number || null,
        rera_images: reraImages.length > 0 ? reraImages.map(r => r.url) : null,
        rera_link: formData.rera_link || null,
        connectivity: Object.values(connectivity).some(v => v) ? connectivity : null,
        location_link: formData.location_link || null,
        show_price_per_sqft: formData.show_price_per_sqft || false,
        price_per_cent: formData.price_per_cent ? parseFloat(formData.price_per_cent) : null,
        rent_period: listingType === 'rent' ? formData.rent_period : null,
        seller_phone: countryCode.replace('+', '') + formData.seller_phone.replace(/^0+/, '')
      };

      // Include user_id as query param if logged in
      const url = user ? `${API}/listings?user_id=${user.id}` : `${API}/listings`;
      await axios.post(url, payload);
      trackEvent('list_property_submit');
      setSubmitted(true);
    } catch (error) {
      console.error('Error creating listing:', error);
      const detail = error.response?.data?.detail;
      let msg = 'Failed to submit listing. Please try again.';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        msg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join('\n');
      }
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-slate-50 pt-16">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (submitted) {
    // Guest user - prompt to register
    if (!user) {
      const sellerPhone = countryCode + formData.seller_phone.replace(/^0+/, '');
      return (
        <div className="min-h-screen bg-slate-50 pt-16">
          <Navbar />
          <div className="px-6 md:px-12 lg:px-24 py-20">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
              <h1 className="text-4xl font-bold mb-4" data-testid="success-message">Listing Submitted!</h1>
              <p className="text-lg text-slate-600 mb-3">
                Your property has been submitted for review.
              </p>
              <p className="text-base text-slate-500 mb-8">
                Register with the same phone number to view and manage your listing from your dashboard.
              </p>
              <div className="flex flex-col gap-3 max-w-sm mx-auto">
                <button
                  onClick={() => navigate(`/login?mode=register&phone=${encodeURIComponent(sellerPhone)}&name=${encodeURIComponent(formData.seller_name)}`)}
                  className="h-12 px-8 rounded-full bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2"
                  data-testid="register-after-listing-btn"
                >
                  <Users className="w-5 h-5" />
                  Register to View Your Listing
                </button>
                <button
                  onClick={() => navigate('/properties')}
                  className="h-12 px-8 rounded-full bg-white border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition-all flex items-center justify-center"
                  data-testid="browse-listings-button"
                >
                  Browse Properties Instead
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Logged-in user - standard success
    return (
      <div className="min-h-screen bg-slate-50 pt-16">
        <Navbar />
        <div className="px-6 md:px-12 lg:px-24 py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold mb-4" data-testid="success-message">Listing Submitted!</h1>
            <p className="text-lg text-slate-600 mb-8">
              Your property has been submitted for review. Our team will verify and approve it shortly.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/properties')}
                className="h-12 px-8 rounded-full bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20"
                data-testid="browse-listings-button"
              >
                Browse Listings
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="h-12 px-8 rounded-full bg-white border border-slate-200 text-slate-900 font-medium hover:bg-slate-50 transition-all"
                data-testid="view-dashboard-button"
              >
                View Dashboard
              </button>
              <button
                onClick={() => window.location.reload()}
                className="h-12 px-8 rounded-full bg-white border border-slate-200 text-slate-900 font-medium hover:bg-slate-50 transition-all"
                data-testid="list-another-button"
              >
                List Another Property
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-16">
      <SEO title="List Your Property" description="List your property for free on PropTick. Reach verified buyers across Kerala for apartments, villas, lands & commercial spaces." path="/list-property" />
      <Navbar />

      <div className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <img src="/proptick-icon.png" alt="PropTick" className="w-28 h-28 mx-auto mb-4 object-contain" />
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4">List Your Property</h1>
            <p className="text-lg text-slate-600">Fill in the details below to list your property</p>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-3 mb-8" data-testid="step-indicator">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${formStep === 1 ? 'bg-blue-600 text-white' : 'bg-green-100 text-green-700'}`}>
              <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">{formStep > 1 ? '✓' : '1'}</span>
              Property Details
            </div>
            <div className="w-8 h-0.5 bg-slate-200"></div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${formStep === 2 ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
              <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">2</span>
              Amenities & Contact
            </div>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-8 space-y-6" data-testid="list-property-form">
            {/* Buy/Rent Toggle */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex bg-slate-100 rounded-full p-1" data-testid="listing-type-toggle">
                <button
                  type="button"
                  onClick={() => setListingType('buy')}
                  className={`px-8 py-3 rounded-full font-medium transition-all ${
                    listingType === 'buy'
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid="toggle-buy"
                >
                  Buy
                </button>
                <button
                  type="button"
                  onClick={() => setListingType('rent')}
                  className={`px-8 py-3 rounded-full font-medium transition-all ${
                    listingType === 'rent'
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid="toggle-rent"
                >
                  Rent
                </button>
              </div>
            </div>

            {/* Property Details */}
            {formStep === 1 && (
            <>
            <div>
              <h3 className="text-xl font-semibold mb-4">Property Details</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Property Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="w-full h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                    placeholder="e.g., Spacious 3BHK Apartment in Prime Location"
                    data-testid="input-title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Description *</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4 py-3"
                    placeholder="Describe your property in detail..."
                    data-testid="input-description"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Seller Type - MOVED UP */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Seller Type *</label>
                    <select
                      name="seller_type"
                      value={formData.seller_type}
                      onChange={handleChange}
                      required
                      className="w-full h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                      data-testid="input-seller-type"
                    >
                      <option value="owner">Owner</option>
                      <option value="builder">Builder</option>
                      <option value="agent">Agent</option>
                    </select>
                  </div>

                  {/* Property Status: Off Plan / Ready */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Property Status *</label>
                    <div className="flex gap-3" data-testid="property-status-toggle">
                      <button
                        type="button"
                        onClick={() => setPropertyStatus('ready')}
                        className={`flex-1 h-12 rounded-lg font-medium border-2 transition-all ${
                          propertyStatus === 'ready'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                        }`}
                        data-testid="status-ready"
                      >
                        Ready
                      </button>
                      <button
                        type="button"
                        onClick={() => setPropertyStatus('off_plan')}
                        className={`flex-1 h-12 rounded-lg font-medium border-2 transition-all ${
                          propertyStatus === 'off_plan'
                            ? 'border-amber-500 bg-amber-50 text-amber-700'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                        }`}
                        data-testid="status-off-plan"
                      >
                        Off Plan
                      </button>
                    </div>
                  </div>

                  {/* Unit Types - For Off Plan OR Builder */}
                  {(propertyStatus === 'off_plan' || formData.seller_type === 'builder') && (
                    <div className="md:col-span-2 bg-amber-50 border border-amber-200 rounded-xl p-5" data-testid="unit-types-section">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-800">Unit Configurations</h4>
                          <p className="text-xs text-slate-500 mt-0.5">Add multiple bedroom/price configurations for this project</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setUnitTypes([...unitTypes, { bedrooms: '', price: '', sqft_min: '', sqft_max: '', bathrooms: '' }])}
                          className="h-9 px-4 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 transition-all flex items-center gap-1.5"
                          data-testid="add-unit-type"
                        >
                          <Plus className="w-4 h-4" /> Add Config
                        </button>
                      </div>
                      {unitTypes.length === 0 && (
                        <p className="text-sm text-slate-400 text-center py-4">No unit configurations added yet. Click &quot;Add Config&quot; to add bedroom/price variants.</p>
                      )}
                      <div className="space-y-3">
                        {unitTypes.map((unit, idx) => (
                          <div key={idx} className="bg-white rounded-lg border border-slate-200 p-4" data-testid={`unit-type-${idx}`}>
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-xs font-semibold text-slate-500 uppercase">Config {idx + 1}</span>
                              <button type="button" onClick={() => setUnitTypes(unitTypes.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700 transition-colors" data-testid={`remove-unit-${idx}`}>
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                              <div>
                                <label className="text-[11px] font-medium text-slate-500 mb-1 block">Beds</label>
                                <select value={unit.bedrooms} onChange={(e) => { const u = [...unitTypes]; u[idx].bedrooms = e.target.value; setUnitTypes(u); }}
                                  className="w-full h-10 rounded-lg border-slate-200 bg-white px-3 text-sm" data-testid={`unit-beds-${idx}`}>
                                  <option value="">-</option>
                                  {[1,1.5,2,2.5,3,3.5,4,5,6].map(n => <option key={n} value={n}>{n} BHK</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="text-[11px] font-medium text-slate-500 mb-1 block">Price (from)</label>
                                <input type="text" inputMode="numeric" placeholder="e.g., 50,00,000" value={formatIndianNumber(unit.price)} onChange={(e) => { const u = [...unitTypes]; u[idx].price = parseNumber(e.target.value); setUnitTypes(u); }}
                                  className="w-full h-10 rounded-lg border-slate-200 bg-white px-3 text-sm" data-testid={`unit-price-${idx}`} />
                              </div>
                              <div>
                                <label className="text-[11px] font-medium text-slate-500 mb-1 block">Sqft Min</label>
                                <input type="number" placeholder="e.g., 1200" value={unit.sqft_min} onChange={(e) => { const u = [...unitTypes]; u[idx].sqft_min = e.target.value; setUnitTypes(u); }}
                                  className="w-full h-10 rounded-lg border-slate-200 bg-white px-3 text-sm" data-testid={`unit-sqft-min-${idx}`} />
                              </div>
                              <div>
                                <label className="text-[11px] font-medium text-slate-500 mb-1 block">Sqft Max</label>
                                <input type="number" placeholder="e.g., 1800" value={unit.sqft_max} onChange={(e) => { const u = [...unitTypes]; u[idx].sqft_max = e.target.value; setUnitTypes(u); }}
                                  className="w-full h-10 rounded-lg border-slate-200 bg-white px-3 text-sm" data-testid={`unit-sqft-max-${idx}`} />
                              </div>
                              <div>
                                <label className="text-[11px] font-medium text-slate-500 mb-1 block">Baths</label>
                                <select value={unit.bathrooms} onChange={(e) => { const u = [...unitTypes]; u[idx].bathrooms = e.target.value; setUnitTypes(u); }}
                                  className="w-full h-10 rounded-lg border-slate-200 bg-white px-3 text-sm" data-testid={`unit-baths-${idx}`}>
                                  <option value="">-</option>
                                  {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Property Type *</label>
                    <select
                      name="property_type"
                      value={formData.property_type}
                      onChange={handleChange}
                      required
                      className="w-full h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                      data-testid="input-property-type"
                    >
                      <option value="apartment">Apartment</option>
                      <option value="villa">Villa</option>
                      <option value="land">Land</option>
                      <option value="commercial">Commercial</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Number of Bedrooms</label>
                    <select
                      name="bedrooms"
                      value={formData.bedrooms}
                      onChange={handleChange}
                      className="w-full h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                      data-testid="input-bedrooms"
                    >
                      <option value="">Select Bedrooms</option>
                      <option value="1">1 BHK</option>
                      <option value="1.5">1.5 BHK</option>
                      <option value="2">2 BHK</option>
                      <option value="2.5">2.5 BHK</option>
                      <option value="3">3 BHK</option>
                      <option value="3.5">3.5 BHK</option>
                      <option value="4">4 BHK</option>
                      <option value="5">5+ BHK</option>
                    </select>
                    {(propertyStatus === 'off_plan' || formData.seller_type === 'builder') && (
                      <label className="flex items-center gap-2 mt-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.bedrooms_onwards}
                          onChange={(e) => setFormData({ ...formData, bedrooms_onwards: e.target.checked })}
                          className="w-4 h-4 rounded border-slate-300 accent-blue-600"
                          data-testid="input-bedrooms-onwards"
                        />
                        <span className="text-sm text-slate-600">Show as &quot;Onwards&quot;</span>
                      </label>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Number of Bathrooms</label>
                    <select
                      name="bathrooms"
                      value={formData.bathrooms}
                      onChange={handleChange}
                      className="w-full h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                      data-testid="input-bathrooms"
                    >
                      <option value="">Select Bathrooms</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5+</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Area in Square Feet <span className="text-slate-400 font-normal">(optional)</span></label>
                  <input
                    type="number"
                    name="size_sqft"
                    value={formData.size_sqft}
                    onChange={handleChange}
                    className="w-full h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                    placeholder="e.g., 1500"
                    data-testid="input-size-sqft"
                  />
                  {(propertyStatus === 'off_plan' || formData.seller_type === 'builder') && (
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.size_sqft_onwards}
                        onChange={(e) => setFormData({ ...formData, size_sqft_onwards: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-300 accent-blue-600"
                        data-testid="input-sqft-onwards"
                      />
                      <span className="text-sm text-slate-600">Show as &quot;Onwards&quot; sqft</span>
                    </label>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Land Size <span className="text-slate-400 font-normal">(optional, e.g., 5 cents, 10 cents)</span></label>
                  <input
                    type="text"
                    name="land_size"
                    value={formData.land_size}
                    onChange={handleChange}
                    className="w-full h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                    placeholder="e.g., 5 cents"
                    data-testid="input-land-size"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">City *</label>
                    <select
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="w-full h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                      data-testid="input-city"
                    >
                      <option value="">Select City</option>
                      <option value="Trivandrum">Trivandrum</option>
                      <option value="Kollam">Kollam</option>
                      <option value="Pathanamthitta">Pathanamthitta</option>
                      <option value="Alappuzha">Alappuzha</option>
                      <option value="Kottayam">Kottayam</option>
                      <option value="Idukki">Idukki</option>
                      <option value="Ernakulam">Ernakulam</option>
                      <option value="Kochi">Kochi</option>
                      <option value="Thrissur">Thrissur</option>
                      <option value="Palakkad">Palakkad</option>
                      <option value="Malappuram">Malappuram</option>
                      <option value="Kozhikode">Kozhikode</option>
                      <option value="Wayanad">Wayanad</option>
                      <option value="Kannur">Kannur</option>
                      <option value="Kasaragod">Kasaragod</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Locality *</label>
                    <input
                      type="text"
                      name="locality"
                      value={formData.locality}
                      onChange={handleChange}
                      required
                      className="w-full h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                      placeholder="e.g., Pattom, MG Road, Beach Road"
                      data-testid="input-locality"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Price (INR) *</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="price"
                    value={formatIndianNumber(formData.price)}
                    onChange={(e) => setFormData({ ...formData, price: parseNumber(e.target.value) })}
                    required
                    className="w-full h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                    placeholder="e.g., 50,00,000"
                    data-testid="input-price"
                  />
                  {(propertyStatus === 'off_plan' || formData.seller_type === 'builder' || listingType === 'rent') && (
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.price_onwards}
                        onChange={(e) => setFormData({ ...formData, price_onwards: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-300 accent-blue-600"
                        data-testid="input-price-onwards"
                      />
                      <span className="text-sm text-slate-600">Show as &quot;Onwards&quot; price</span>
                    </label>
                  )}
                </div>

                {/* Rent Period - only for rent listings */}
                {listingType === 'rent' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Rent Period *</label>
                    <div className="flex gap-3" data-testid="rent-period-toggle">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, rent_period: 'per_month' })}
                        className={`flex-1 h-12 rounded-lg font-medium border-2 transition-all ${
                          formData.rent_period === 'per_month'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                        }`}
                        data-testid="rent-per-month"
                      >
                        Per Month
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, rent_period: 'per_day' })}
                        className={`flex-1 h-12 rounded-lg font-medium border-2 transition-all ${
                          formData.rent_period === 'per_day'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                        }`}
                        data-testid="rent-per-day"
                      >
                        Per Day
                      </button>
                    </div>
                  </div>
                )}

                {/* Price Options */}
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center gap-2 cursor-pointer h-12">
                    <input
                      type="checkbox"
                      checked={formData.show_price_per_sqft}
                      onChange={(e) => setFormData({ ...formData, show_price_per_sqft: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300 accent-blue-600"
                      data-testid="input-show-price-per-sqft"
                    />
                    <span className="text-sm text-slate-700">Display price per sq ft on listing</span>
                  </label>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Price per Cent (optional)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      name="price_per_cent"
                      value={formatIndianNumber(formData.price_per_cent)}
                      onChange={(e) => setFormData({ ...formData, price_per_cent: parseNumber(e.target.value) })}
                      className="w-full h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                      placeholder="e.g., 5,00,000"
                      data-testid="input-price-per-cent"
                    />
                  </div>
                </div>

                {/* RERA Section */}
                <div className="md:col-span-2 bg-purple-50 border border-purple-200 rounded-xl p-5">
                  <h4 className="text-sm font-semibold text-slate-800 mb-3">RERA Information <span className="text-slate-400 font-normal">(Optional)</span></h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">RERA Number</label>
                      <input
                        type="text"
                        name="rera_number"
                        value={formData.rera_number || ''}
                        onChange={handleChange}
                        className="w-full h-11 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-purple-100 focus:border-purple-500 transition-all px-4 text-sm"
                        placeholder="e.g., K-RERA/PRJ/TVM/087/2025"
                        data-testid="input-rera-number"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">RERA Link</label>
                      <input
                        type="url"
                        name="rera_link"
                        value={formData.rera_link || ''}
                        onChange={handleChange}
                        className="w-full h-11 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-purple-100 focus:border-purple-500 transition-all px-4 text-sm"
                        placeholder="https://rera.kerala.gov.in/..."
                        data-testid="input-rera-link"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-2">RERA Images (max 5) — QR codes, certificates, etc.</label>
                    <div className="flex flex-wrap gap-3">
                      {reraImages.map((img, idx) => (
                        <div key={img.id} className="relative w-24 h-24 rounded-lg overflow-hidden border border-slate-200 bg-white">
                          <img src={resolveImg(img.url)} alt={`RERA ${idx + 1}`} className="w-full h-full object-cover" />
                          <button type="button" onClick={() => setReraImages(reraImages.filter((_, i) => i !== idx))} className="absolute top-1 right-1 w-5 h-5 bg-red-600 text-white rounded-full flex items-center justify-center text-xs">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {reraImages.length < 5 && (
                        <label htmlFor="rera-file-input" className={`w-24 h-24 flex flex-col items-center justify-center rounded-lg border-2 border-dashed cursor-pointer transition-all ${uploadingRera ? 'border-purple-400 bg-purple-50' : 'border-slate-300 hover:border-purple-400'}`}>
                          <input id="rera-file-input" type="file" accept="image/*" className="hidden" disabled={uploadingRera} onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setUploadingRera(true);
                            try {
                              const fd = new FormData();
                              fd.append('file', file);
                              const res = await axios.post(`${API}/upload-image`, fd);
                              setReraImages(prev => [...prev, { url: res.data.url, id: `rera-${Date.now()}` }]);
                            } catch (err) { console.error(err); alert('Upload failed'); }
                            finally { setUploadingRera(false); e.target.value = ''; }
                          }} />
                          {uploadingRera ? <Loader2 className="w-5 h-5 animate-spin text-purple-500" /> : <Upload className="w-5 h-5 text-slate-400" />}
                          <span className="text-[10px] text-slate-400 mt-1">{uploadingRera ? 'Uploading' : 'Add'}</span>
                        </label>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Property Images (max 20)</label>
                  <p className="text-xs text-slate-500 mb-3">A clear, bright image goes a long way. Great properties deserve great pictures.</p>
                  
                  {/* Upload area */}
                  {uploadedImages.length < 20 && (
                    <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                      uploading ? 'border-blue-400 bg-blue-50' : 'border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50'
                    }`} data-testid="image-upload-area">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploading}
                        data-testid="image-file-input"
                      />
                      {uploading ? (
                        <div className="flex flex-col items-center w-full px-8">
                          <Loader2 className="w-7 h-7 text-blue-600 animate-spin mb-2" />
                          <span className="text-sm text-blue-700 font-semibold mb-1">
                            Uploading image {uploadProgress.current} of {uploadProgress.total}
                          </span>
                          <div className="w-full max-w-xs bg-slate-200 rounded-full h-2.5 overflow-hidden">
                            <div
                              className="bg-blue-600 h-full rounded-full transition-all duration-300"
                              style={{ width: `${uploadProgress.percent}%` }}
                              data-testid="upload-progress-bar"
                            />
                          </div>
                          <span className="text-xs text-slate-500 mt-1">{uploadProgress.percent}% — please don't close this page</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <Upload className="w-8 h-8 text-slate-400 mb-2" />
                          <span className="text-sm text-slate-600 font-medium">Click to upload images</span>
                          <span className="text-xs text-slate-400 mt-1">JPEG, PNG, WebP, GIF (max 10MB each)</span>
                        </div>
                      )}
                    </label>
                  )}

                  {/* Preview grid */}
                  {uploadedImages.length > 0 && (
                    <div className="grid grid-cols-4 gap-3 mt-3" data-testid="image-preview-grid">
                      {uploadedImages.map((img, i) => (
                        <div key={i} className={`relative group rounded-lg overflow-hidden h-24 bg-slate-100 ${i === 0 ? 'ring-2 ring-blue-500' : ''}`}>
                          <img src={img.preview || img.url} alt={`Upload ${i + 1}`} className="w-full h-full object-cover" />
                          {i === 0 && (
                            <span className="absolute top-1 left-1 bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">Cover</span>
                          )}
                          {i !== 0 && (
                            <button
                              type="button"
                              onClick={() => setAsCover(i)}
                              className="absolute bottom-1 left-1 text-[10px] font-semibold bg-white/90 text-slate-700 px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-blue-600 hover:text-white"
                              data-testid={`set-cover-${i}`}
                            >
                              Set as Cover
                            </button>
                          )}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all pointer-events-none" />
                          <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() => removeImage(i)}
                              className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center"
                              data-testid={`remove-image-${i}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <p className="text-xs text-slate-500 mt-2">
                    {uploadedImages.length}/20 images uploaded
                  </p>
                </div>
              </div>
            </div>

            {/* Next Button - Step 1 */}
            <div className="border-t border-slate-200 pt-6">
              <button
                type="button"
                onClick={() => { setFormStep(2); window.scrollTo(0, 0); }}
                className="w-full h-14 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2 text-lg"
                data-testid="next-step-button"
              >
                Next: Amenities & Contact Details
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            </>
            )}
            {formStep === 2 && (
            <>
            <div>
              <div className="space-y-4">
                {/* Amenities Section */}
                <div>
                  <h3 className="text-xl font-semibold mb-2">Amenities</h3>
                  <p className="text-sm text-slate-500 mb-4">Select all amenities available in your property</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="amenities-section">
                    {AMENITIES_LIST.map((amenity) => {
                      const isSelected = selectedAmenities.includes(amenity.id);
                      const IconComponent = amenity.icon;
                      return (
                        <button
                          key={amenity.id}
                          type="button"
                          onClick={() => toggleAmenity(amenity.id)}
                          className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                          }`}
                          data-testid={`amenity-${amenity.id}`}
                        >
                          <IconComponent className={`w-5 h-5 ${isSelected ? 'text-blue-600' : 'text-slate-400'}`} />
                          <span className="text-sm font-medium">{amenity.label}</span>
                          {isSelected && (
                            <CheckCircle2 className="w-4 h-4 text-blue-600 ml-auto" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {selectedAmenities.length > 0 && (
                    <p className="text-sm text-blue-600 mt-3">
                      {selectedAmenities.length} amenities selected
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Connectivity */}
            <div className="border-t border-slate-200 pt-6">
              <h3 className="text-xl font-semibold mb-2">Connectivity</h3>
              <p className="text-sm text-slate-500 mb-5">Distance to nearby facilities (optional)</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                {[
                  { key: 'railway_station', label: 'Railway Station' },
                  { key: 'hospital', label: 'Hospital' },
                  { key: 'police_station', label: 'Police Station' },
                  { key: 'school', label: 'School' },
                  { key: 'mall', label: 'Mall' },
                  { key: 'airport', label: 'Airport' }
                ].map(({ key, label }) => {
                  const valMap = { 'Under 5 km': 1, 'Under 10 km': 2, 'Under 15 km': 3, 'Above 15 km': 4 };
                  const reverseMap = { 0: '', 1: 'Under 5 km', 2: 'Under 10 km', 3: 'Under 15 km', 4: 'Above 15 km' };
                  const current = valMap[connectivity[key]] || 0;
                  return (
                    <div key={key} data-testid={`connectivity-${key}`}>
                      <div className="flex justify-between items-center mb-1.5">
                        <label className="text-sm font-medium text-slate-700">{label}</label>
                        <span className="text-xs font-semibold text-blue-600">{connectivity[key] || 'Not set'}</span>
                      </div>
                      <input
                        type="range" min="0" max="4" step="1" value={current}
                        onChange={(e) => setConnectivity({ ...connectivity, [key]: reverseMap[Number(e.target.value)] })}
                        className="w-full h-2 bg-slate-200 rounded-full appearance-none cursor-pointer accent-blue-600"
                      />
                      <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                        <span>-</span><span>5 km</span><span>10 km</span><span>15 km</span><span>15+</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Location Link */}
              <div className="mt-5">
                <label className="block text-sm font-medium text-slate-700 mb-2">Google Maps Location Link (optional)</label>
                <input
                  type="url"
                  name="location_link"
                  value={formData.location_link}
                  onChange={handleChange}
                  className="w-full h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                  placeholder="Paste Google Maps link e.g. https://maps.google.com/..."
                  data-testid="input-location-link"
                />
                <p className="text-xs text-slate-400 mt-1">If provided, the map on your listing will show this exact location. Otherwise, the locality will be used.</p>
              </div>
            </div>

            {/* Seller Information */}
            <div className="border-t border-slate-200 pt-6">
              <h3 className="text-xl font-semibold mb-4">Seller Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="seller_name"
                    value={formData.seller_name}
                    onChange={handleChange}
                    required
                    className="w-full h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                    placeholder="Your full name"
                    data-testid="input-seller-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number *</label>
                  <div className="flex gap-2">
                    <select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="w-[130px] h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-2 text-sm"
                      data-testid="input-country-code"
                    >
                      <option value="+91">+91 IN</option>
                      <option value="+971">+971 AE</option>
                      <option value="+1">+1 US</option>
                      <option value="+44">+44 UK</option>
                      <option value="+966">+966 SA</option>
                      <option value="+968">+968 OM</option>
                      <option value="+974">+974 QA</option>
                      <option value="+973">+973 BH</option>
                      <option value="+965">+965 KW</option>
                      <option value="+61">+61 AU</option>
                      <option value="+65">+65 SG</option>
                      <option value="+60">+60 MY</option>
                      <option value="+49">+49 DE</option>
                      <option value="+33">+33 FR</option>
                      <option value="+39">+39 IT</option>
                      <option value="+81">+81 JP</option>
                      <option value="+86">+86 CN</option>
                      <option value="+82">+82 KR</option>
                      <option value="+7">+7 RU</option>
                      <option value="+55">+55 BR</option>
                      <option value="+27">+27 ZA</option>
                      <option value="+234">+234 NG</option>
                      <option value="+254">+254 KE</option>
                      <option value="+63">+63 PH</option>
                      <option value="+92">+92 PK</option>
                      <option value="+94">+94 LK</option>
                      <option value="+880">+880 BD</option>
                      <option value="+977">+977 NP</option>
                    </select>
                    <input
                      type="tel"
                      name="seller_phone"
                      value={formData.seller_phone}
                      onChange={handleChange}
                      required
                      className="flex-1 h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                      placeholder="e.g., 9876543210"
                      data-testid="input-seller-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email <span className="text-slate-400 font-normal">(optional)</span></label>
                  <input
                    type="email"
                    name="seller_email"
                    value={formData.seller_email}
                    onChange={handleChange}
                    className="w-full h-12 rounded-lg border-slate-200 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-4"
                    placeholder="your.email@example.com"
                    data-testid="input-seller-email"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-6 flex gap-3">
              <button
                type="button"
                onClick={() => { setFormStep(1); window.scrollTo(0, 0); }}
                className="h-14 px-8 rounded-full bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all flex items-center justify-center gap-2"
                data-testid="back-step-button"
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 h-14 rounded-full bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-button"
              >
                {loading ? 'Submitting...' : 'Submit for Approval'}
              </button>
            </div>
              <p className="text-xs text-slate-500 mt-3 text-center">
                Your listing will be reviewed by our team before being published
              </p>
            </>
            )}
          </form>

          {/* Contact Us */}
          <div className="mt-8 bg-slate-50 rounded-2xl border border-slate-200 p-6 text-center" data-testid="list-property-contact">
            <p className="text-sm text-slate-600 mb-2">Need help listing your property?</p>
            <a
              href="mailto:admin@proptick.in"
              className="inline-flex items-center gap-2 text-blue-600 font-medium hover:text-blue-700 transition-colors"
              data-testid="list-property-contact-email"
            >
              <Mail className="w-4 h-4" /> admin@proptick.in
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListProperty;
